import axios from 'axios'
import parser from 'fast-xml-parser'
import { bggHttp } from './bggHttp'
import { bggTextField } from './Utils'

/** Keep in sync with docs/bgg-thing-spike.md */
export const THING_ID_CHUNK = 20
const THING_RETRY_MAX = 5

/** @type {Map<string, ThingMeta>} */
const thingMetaCache = new Map()

export function clearThingMetaCache() {
	thingMetaCache.clear()
}

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function normalizeThingItems(parsed) {
	if (!parsed || !parsed.items || parsed.items.item === undefined) return []
	const it = parsed.items.item
	return Array.isArray(it) ? it : [it]
}

/**
 * @param {unknown} node
 * @returns {number|null}
 */
function attrNumber(node) {
	if (node == null || typeof node !== 'object') return null
	const v = node['@_value']
	if (v == null || v === '') return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

function digAverageWeight(item) {
	const stats = item?.statistics
	if (!stats || typeof stats !== 'object') return null
	const ratings = stats.ratings
	if (!ratings || typeof ratings !== 'object') return null
	const aw = ratings.averageweight
	return attrNumber(aw)
}

/**
 * @typedef {{
 *   id: string
 *   minPlayers: number | null
 *   maxPlayers: number | null
 *   playingTime: number | null
 *   minPlayTime: number | null
 *   maxPlayTime: number | null
 *   averageWeight: number | null
 *   image: string
 *   thumbnail: string
 * }} ThingMeta
 */

/**
 * @param {unknown} item
 * @returns {ThingMeta | null}
 */
function itemToMeta(item) {
	if (!item || typeof item !== 'object') return null
	const id = item['@_id'] != null ? String(item['@_id']) : ''
	if (!id) return null
	const image = bggTextField(item.image).trim()
	const thumbnail = bggTextField(item.thumbnail).trim()
	return {
		id,
		minPlayers: attrNumber(item.minplayers),
		maxPlayers: attrNumber(item.maxplayers),
		playingTime: attrNumber(item.playingtime),
		minPlayTime: attrNumber(item.minplaytime),
		maxPlayTime: attrNumber(item.maxplaytime),
		averageWeight: digAverageWeight(item),
		image,
		thumbnail,
	}
}

async function fetchThingChunkXml(ids) {
	const idStr = ids.join(',')
	let lastErr
	for (let attempt = 1; attempt <= THING_RETRY_MAX; attempt++) {
		try {
			const res = await bggHttp.get('/thing', {
				params: { id: idStr, stats: 1 },
			})
			return res.data
		} catch (e) {
			lastErr = e
			if (axios.isCancel(e)) {
				await sleep(800 * attempt)
				continue
			}
			throw e
		}
	}
	throw lastErr || new Error('thing fetch failed')
}

/**
 * Batch-fetch `/thing` with `stats=1` (ratings + cover URLs). Results are merged into an in-memory cache;
 * only missing ids trigger HTTP. Call `clearThingMetaCache` when loading a fresh collection.
 * @param {string[]} objectIds
 * @returns {Promise<Map<string, ThingMeta>>}
 */
export async function fetchThingMetaByGameIds(objectIds) {
	const out = new Map()
	if (!objectIds || !objectIds.length) return out

	const seen = new Set()
	const uniqueIds = []
	for (const raw of objectIds) {
		const id = String(raw || '').trim()
		if (!id || seen.has(id)) continue
		seen.add(id)
		uniqueIds.push(id)
	}

	for (const id of uniqueIds) {
		const cached = thingMetaCache.get(id)
		if (cached) out.set(id, cached)
	}

	const missing = uniqueIds.filter((id) => !thingMetaCache.has(id))
	if (!missing.length) return out

	for (let i = 0; i < missing.length; i += THING_ID_CHUNK) {
		const chunk = missing.slice(i, i + THING_ID_CHUNK)
		let xml
		try {
			xml = await fetchThingChunkXml(chunk)
		} catch {
			continue
		}
		let parsed
		try {
			parsed = parser.parse(xml, { ignoreAttributes: false })
		} catch {
			continue
		}
		for (const item of normalizeThingItems(parsed)) {
			const meta = itemToMeta(item)
			if (meta) thingMetaCache.set(meta.id, meta)
		}
	}

	for (const id of uniqueIds) {
		const m = thingMetaCache.get(id)
		if (m) out.set(id, m)
	}

	return out
}
